/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';

import { filtersPanelAnalytics } from '../../analyticsConsts';
import useGeolocation, { LOADING_STATE } from '../../hooks/useGeolocation';
import filterMenuStyle from '../FiltersMenu/styles';
import { ErrorIcon, FilterLocationIcon } from '../Icons';
import LinkButton from '../LinkButton';
import Loader from '../Loader';
import LocationAutoSuggest from '../LocationAutoSuggest';
import useStyles from './styles';
const { FILTERS_PANEL_CTA_CURRENT_LOCATION } = filtersPanelAnalytics;
const ERROR_TYPES = {
  NONE: 'none',
  INPUT: 'input',
  GEO: 'geo',
};

const FilterByLocation = ({
  onSave,
  currentValue: value,
  onErrorChange,
  locationHasError,
  currentGeoLocation,
  setCurrentGeoLocation,
  preferences,
  filterShowHide,
  onManualInput,
  onUnlockLocation,
  readOnly,
  magnifierIconVariant,
  LoaderComponent = Loader,
  isSmall,
}) => {
  const styles = useStyles({ inFilterMenu: filterShowHide });
  const filterMenuStyles = filterMenuStyle();
  //To check error type. Based on that to show error status messages in UI
  const [errorType, setErrorType] = useState(ERROR_TYPES.NONE);
  const [isGeoLocationProcessing, setIsGeoLocationProcessing] = useState(false);
  // *** ADDED: Flag to skip parent error sync temporarily ***

  const [locationData, setLocationData] = useState({});
  // Ref to ensure we handle a single denial only once to avoid
  // repeatedly calling `onSave` and triggering parent updates.
  const geoDeniedHandledRef = useRef(false);
  // saves the value from the browser geolocation
  const { geolocationData, getGeolocationData, setGeolocationData } =
    useGeolocation();
  const { coordinates, status, statusMessage } = geolocationData || {};
  const locationSelected =
    locationData?.readableLocation || geolocationData?.status === 'success';
  const [currentValue, setCurrentValue] = useState(value);

  const [forceMyLocation, setForceMyLocation] = useState(false);
  const [showUseCurrentLocationButton, setShowUseCurrentLocationButton] =
    useState(true);
  const [currentButtonStatusMessage, setCurrentButtonStatusMessage] =
    useState(false);

  // To control styles passed to LocationAutoSuggest
  const isErrorActive = errorType !== ERROR_TYPES.NONE;
  const isDisabled = isGeoLocationProcessing;

  // Effect to handle SUCCESSFUL geolocation data
  useEffect(() => {
    if (geolocationData && geolocationData.status === 'success') {
      if (coordinates?.latitude && coordinates?.longitude) {
        onSave({
          Latitude: coordinates.latitude,
          Longitude: coordinates.longitude,
          ReadableLocation: 'Your location',
          OptforCurrentLocation: 'Yes',
        });
        setCurrentGeoLocation(true);
        onErrorChange(false);
        setErrorType(ERROR_TYPES.NONE);
      }
    } else if (geolocationData && geolocationData.status !== 'loading') {
      setCurrentGeoLocation(false);
      if (
        geolocationData.status === 'error' ||
        geolocationData.status === 'denied'
      ) {
        // If user has already selected a valid location manually, don't show the error
        if (locationData?.readableLocation) {
          return;
        }
        // Prevent repeated handling of the same denial
        if (geoDeniedHandledRef.current) return;
        geoDeniedHandledRef.current = true;
        // Clear the input value to show placeholder text
        setCurrentValue('');
        onSave({
          Latitude: '',
          Longitude: '',
          ReadableLocation: 'Search by area or postcode',
          OptforCurrentLocation: 'No',
        });
        onErrorChange(true);
        setErrorType(ERROR_TYPES.GEO);
      }
    }
  }, [
    geolocationData,
    coordinates,
    onErrorChange,
    onSave,
    setCurrentGeoLocation,
    locationData,
  ]);

  //Effect to update the displayed value in the text input
  useEffect(() => {
    if (locationData?.readableLocation) {
      setCurrentValue(locationData.readableLocation);
    } else if (geolocationData?.status === 'success' && currentGeoLocation) {
      setCurrentValue('Your location');
    } else if (!value) {
      setCurrentValue('');
    }
  }, [locationData, geolocationData, currentGeoLocation, value]);

  const handleUseCurrentLocation = async () => {
    setLocationData({});
    setForceMyLocation(true);
    setCurrentButtonStatusMessage(true);
    setShowUseCurrentLocationButton(false);
    onErrorChange(false);
    setErrorType(ERROR_TYPES.NONE);
    // Start Geoprocessing flag
    setIsGeoLocationProcessing(true);

    const geoResult = await getGeolocationData();

    if (
      !geoResult ||
      geoResult.status === 'error' ||
      geoResult.status === 'denied'
    ) {
      onErrorChange(true);
      setErrorType(ERROR_TYPES.GEO); // Explicitly set GEO error
      setShowUseCurrentLocationButton(true);
      //  Ending processing flag on erro state
      setIsGeoLocationProcessing(false);
      onUnlockLocation?.();
      return;
    }
    setErrorType(ERROR_TYPES.NONE);
    onErrorChange(false);
    // End processing flag on success
    setIsGeoLocationProcessing(false);
  };

  // useEffect to handle parent prop (locationHasError) vs local state (errorType)
  useEffect(() => {
    //  If a geo attempt is active, DO NOT run this effect.
    if (isGeoLocationProcessing) return;

    if (errorType === ERROR_TYPES.GEO) return;

    if (locationHasError) {
      setErrorType(ERROR_TYPES.INPUT);
    } else if (errorType === ERROR_TYPES.INPUT) {
      setErrorType(ERROR_TYPES.NONE);
    }
  }, [locationHasError, errorType, isGeoLocationProcessing]);

    const handleForcedClear = () => {
      setShowUseCurrentLocationButton(true);
      setCurrentButtonStatusMessage(false);
      onSave({ ReadableLocation: '' });
      onManualInput?.();
      setErrorType(ERROR_TYPES.NONE);
      onErrorChange(false);
      setIsGeoLocationProcessing(false);
    };

    const handleAutoSuggestClear = () => {
      setLocationData({});
      setShowUseCurrentLocationButton(true);
      setCurrentButtonStatusMessage('');
      setCurrentGeoLocation(false);
      onErrorChange(false);
      setErrorType(ERROR_TYPES.NONE);
      getGeolocationData.reset?.();
      setCurrentGeoLocation(null);
      setGeolocationData({
        coordinates: null,
        status: '',
        statusMessage: '',
      });
      onSave({ ReadableLocation: '' });
      onManualInput?.();
      setIsGeoLocationProcessing(false);
    };

    // when a postcode/location is selected from autosuggest, save it
    const handleAutoSuggestSelect = item => {
      if (!item || !item.readableLocation) {
        return;
      }
      setLocationData(item);
      setShowUseCurrentLocationButton(false);
      setForceMyLocation(false);
      setCurrentGeoLocation(false);
      // Update currentValue to display the selected location in input
      setCurrentValue(item.readableLocation);
      // Clear all errors when valid location is selected
      onErrorChange(false);
      setErrorType(ERROR_TYPES.NONE);
      setCurrentButtonStatusMessage(false);
      setIsGeoLocationProcessing(false);
      if (item?.readableLocation) {
        onSave({
          ReadableLocation: item.readableLocation,
          Latitude: item.latitude,
          Longitude: item.longitude,
          OptforCurrentLocation: 'No',
        });
      }
    };

    const handleInputEmpty = () => {
      onErrorChange(true);
      setErrorType(ERROR_TYPES.INPUT);
      setShowUseCurrentLocationButton(true);
    };

    const handleInputChange = (e) => {
    const newValue = e?.target?.value || '';
    if (newValue.trim() === '') {
      setErrorType(ERROR_TYPES.INPUT);
      onErrorChange(true);
    } else {
      setErrorType(ERROR_TYPES.NONE);
      onErrorChange(false);
    }
    setCurrentButtonStatusMessage(false);
  };

  return (
    <Fragment>
      <div css={filterMenuStyles.filterHead}>
        <h5 css={filterMenuStyles.filterTitle}>Location</h5>
      </div>
      <div css={filterMenuStyles.filterBody}>
        <LocationAutoSuggest
          onManualInput={() => {
            onUnlockLocation?.();
            onManualInput?.();
          }}
          onChange={handleInputChange}
          onSelect={handleAutoSuggestSelect}
          initialValue={value}
          isFilter={true}
          currentValue={
            preferences.OptforCurrentLocation === 'Yes'
              ? 'Near me'
              : currentValue
          }
          placeholder={
            preferences.OptforCurrentLocation === 'Yes'
              ? 'Near me'
              : 'Search by area or postcode'
          }
          setLocationData={value => {
            handleAutoSuggestSelect(value);
          }}
          setLocationError={onErrorChange}
          locationHasError={isErrorActive}
          currentGeoLocation={currentGeoLocation}
          locationData={locationData}
          filterShowHide={filterShowHide}
          forceMyLocation={forceMyLocation}
          onForcedClear={handleForcedClear}
          onClear={() => {
            onUnlockLocation?.();
            handleAutoSuggestClear();
          }}
          onInputEmpty={handleInputEmpty}
          disabled={isDisabled}
          readOnly={readOnly}
          magnifierIconVariant={magnifierIconVariant}
        />
        <div css={styles.locationLink}>
          {currentButtonStatusMessage && (
            <div aria-live="polite" css={styles.statusMessage(locationHasError)}>
              {status === LOADING_STATE ? (
                <LoaderComponent inline message={statusMessage} isSmall={isSmall}/>
              ) : errorType === ERROR_TYPES.NONE ? (
                statusMessage
              ) : null}
            </div>
          )}
          {/* logic determines which status message displays when erro type is input */}
          {errorType === ERROR_TYPES.INPUT && (
            <div css={styles.errorMessage} aria-live="polite">
              {' '}
              <ErrorIcon />
              Please enter a location to search from
            </div>
          )}
          {/* logic determines which status message displays when erro type is geo */}
          {errorType === ERROR_TYPES.GEO && (
            <div css={styles.errorMessage} aria-live="polite">
              <ErrorIcon />
              {statusMessage}
            </div>
          )}
        
          {!locationSelected && showUseCurrentLocationButton && (
            <Fragment>
              <LinkButton
                id={FILTERS_PANEL_CTA_CURRENT_LOCATION}
                css={styles.locationBtn}
                onClick={handleUseCurrentLocation}
                tabIndex={0}
              >
                Use current location
                <FilterLocationIcon />
              </LinkButton>
            </Fragment>
          )}
          </div>
      </div>
    </Fragment>
  );
};
FilterByLocation.propTypes = {
  currentValue: PropTypes.string,
  onSave: PropTypes.func,
  onErrorChange: PropTypes.func,
  locationHasError: PropTypes.bool,
  currentGeoLocation: PropTypes.bool,
  setCurrentGeoLocation: PropTypes.func,
};

export default FilterByLocation;
